import RenderOnViewportEntry from '@creator/ui/components/RenderOnViewportEntry/RenderOnViewportEntry';
import Spinner from '@creator/ui/components/Spinner/Spinner';
import { ReactLazyPreload } from '@creator/ui/utils/react';
import AdvertisingSlot from '@src/components/Ads/AdvertisingSlot';
import { triggerPageViewAds } from '@src/components/Ads/PlaywireRamp';
import PostView from '@src/components/Post/PostView/PostView';
import TokenSidebar from '@src/components/Token/TokenSidebar/TokenSidebar';
import usePageTitle from '@src/hooks/usePageTitle';
import usePlaywireRamp from '@src/hooks/usePlaywireRamp';
import { usePostVisibilityGuard } from '@src/hooks/usePostVisibilityGuard';
import { shouldDisplayAds } from '@src/model/config/helpers';
import { parsePostContent } from '@src/model/upvote/helpers';
import { getUserId } from '@src/model/user/helpers';
import { usePost } from '@src/store/posts/hooks/usePost';
import { usePostLoader } from '@src/store/posts/hooks/usePostLoader';
import { postView } from '@src/store/posts/view';
import { isMobile } from '@src/utils/utils';
import { type FC, Fragment, memo, useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import BottomStickyAd from '../BottomStickyAd';
import StoryPageHeader from './PostTabHero';

const PartnerList = ReactLazyPreload(() => import('@src/components/Token/PartnerList/PartnerList'));
const TopicsBar = ReactLazyPreload(() => import('../PostsTab/TopicsBar'));
const PartnerPosts = ReactLazyPreload(() => import('../PartnerPosts'));
const CommunitySection = ReactLazyPreload(() => import('../PostsTab/CommunitySection'));

export interface PostTabProps {
    tokenName: string;
    identifier: string;
    commentId?: string;
    replyId?: string;
    className?: string;
}

const PostTab: FC<PostTabProps> = props => {
    const { tokenName, identifier = '', commentId = '', replyId = '' } = props;

    const { load, loadStatus, postId } = usePostLoader({ identifier });
    const [isPostViewExpanded, setIsPostViewExpanded] = useState(false);
    const { isLoaded } = usePlaywireRamp();

    const post = usePost(postId);
    const parentPostId = post?.parentPostId || '';
    const isStory = post?.kind === 'story';
    const { load: loadParentPost } = usePostLoader({ identifier: parentPostId });
    const postContent = useMemo(() => post && parsePostContent(post), [post]);

    // Guard against unauthorized access to hidden posts
    usePostVisibilityGuard({ postId, tokenName });

    usePageTitle(getPageTitle());

    function getPageTitle() {
        if (!post) return '';
        if (post.kind === 'story') return post.title;
        return `${post.title} - Community Post`;
    }

    useLayoutEffect(() => {
        CommunitySection.preload();
        TopicsBar.preload();
        PartnerPosts.preload();
        PartnerList.preload();
    }, [commentId]);

    useEffect(() => {
        setIsPostViewExpanded(false);
    }, [postId]);

    const onPostViewExpend = useCallback(() => {
        setIsPostViewExpanded(true);
    }, [postId]);

    useEffect(() => {
        if (!postId) return;
        postView({ postId, tokenName, userId: getUserId() });
    }, [postId]);

    // Trigger ads on post load
    useEffect(() => {
        if (!isLoaded || !postId) return;
        const enableVideoAd = postContent?.blocks.some(block => block.type === 'video');
        triggerPageViewAds('postTab', enableVideoAd ? 'precontent_ad_video' : 'corner_ad_video');
    }, [isLoaded, postId]);

    useLayoutEffect(() => {
        if (!identifier) return;
        loadContent();
    }, [identifier]);

    useLayoutEffect(() => {
        if (!parentPostId || parentPostId === 'root' || parentPostId === 'null') return;
        loadParentPost();
    }, [parentPostId]);

    async function loadContent() {
        const post = await load();
        if (!post) return;
    }

    function renderPost() {
        if (!postId) return null;
        return <PostView onExpand={onPostViewExpend} highlightCommentId={commentId} loadReplyId={replyId} tokenName={tokenName} postId={postId} />;
    }

    function renderSpinner() {
        if (loadStatus !== 'loading' || post) return null;
        return <Spinner className="mx-auto mt-8" />;
    }

    function renderTokenSidebar() {
        if (isMobile()) return;
        const showRecommendedPosts = isPostViewExpanded || !shouldDisplayAds(tokenName);
        return <TokenSidebar postId={postId} showRecommendedPosts={showRecommendedPosts} tokenName={tokenName} className="hidden lg:block lg:w-4/12" />;
    }

    function renderHeader() {
        if (!post) return null;
        if (post.kind === 'story') return <StoryPageHeader postId={postId} tokenName={tokenName} />;
    }

    function renderMoreContent() {
        if (!post) return null;
        if (post.kind === 'story') {
            const flairId = post.flairIds?.[0];
            return (
                <Fragment>
                    <RenderOnViewportEntry>
                        <PartnerList className="border-gray-200 dark:border-gray-600 border-y" tokenName={tokenName} />
                        <TopicsBar className="" tokenName={tokenName} flairId={flairId} />
                    </RenderOnViewportEntry>
                    <RenderOnViewportEntry>
                        <PartnerPosts excludePostId={postId} className="" tokenName={tokenName} flairId={flairId} />
                    </RenderOnViewportEntry>
                    <RenderOnViewportEntry className="bg-[#F6F7FF] dark:bg-gray-800">
                        <CommunitySection tokenName={tokenName} flairId={flairId} />
                    </RenderOnViewportEntry>
                    <BottomStickyAd postId={postId} tokenName={tokenName} />
                </Fragment>
            );
        }

        return (
            <RenderOnViewportEntry className="bg-[#F6F7FF] dark:bg-gray-800">
                <CommunitySection hideSeparator={true} tokenName={tokenName} />
                <BottomStickyAd postId={postId} tokenName={tokenName} />
            </RenderOnViewportEntry>
        );
    }

    function renderAdvertisingSlot() {
        if (!postId || !shouldDisplayAds(tokenName)) return <div className="my-4"></div>;
        return <AdvertisingSlot key={postId} className="my-4" location="top_center" tokenName={tokenName} />;
    }

    return (
        <div className="mx-auto">
            {renderHeader()}
            {renderAdvertisingSlot()}
            <div className="flex border-t border-gray-200 dark:border-gray-600">
                <div className="w-full lg:w-8/12">
                    {renderPost()}
                    {renderSpinner()}
                </div>
                {renderTokenSidebar()}
            </div>
            {renderMoreContent()}
        </div>
    );
};

export default memo(PostTab);
